import React from 'react'
import { css } from '@emotion/core'
import { Header, HeaderProps } from '@/components/molecules/front-tablet/header'
import { Footer, FooterProps } from '@/components/molecules/front-tablet/footer'
import { SelfCheckInState } from '@/models/self-checkin'
import { useInitializeFrontTablet } from '@/hooks/use-initialize-front-tablet'
import { createSelfCheckinResponsiveStyle, FrontTabletResponsiveToken } from '@/utils/front-tablet-responsive-style'
import { SelfCheckinPageSubtitle, SelfCheckinPageSubtitleProps } from '@/components/molecules/self-checkin-page-subtitle'

type SelfCheckinLayoutProps = {
  state?: SelfCheckInState
  header?: Pick<HeaderProps, 'title' | 'goToPreviousPage'>
  subtitle?: SelfCheckinPageSubtitleProps
  footer?: FooterProps
  /**
   * トップ、QR読み取り、カメラ、完了画面のように、画面全体を使う場合だけtrueを指定する。
   *
   * fullscreen=trueでは背景画像やカメラ映像を画面上端から表示するため、paddingを設定しない。
   * それ以外の画面では、ヘッダーの下から表示するため、paddingを設定する。
   */
  fullscreen?: boolean
}

export const SelfCheckinLayout: React.FC<SelfCheckinLayoutProps> = ({ children, state, header, subtitle, footer, fullscreen = false }) => {
  useInitializeFrontTablet(state)

  return (
    <div data-self-checkin-responsive="true" css={rootStyle}>
      {header && <Header {...header} />}
      <main css={[mainStyle, fullscreen ? fullscreenMainStyle : standardMainStyle, footer && !fullscreen && mainWithFooterStyle]}>
        {fullscreen ? (
          children
        ) : (
          <div css={contentStyle}>
            {subtitle && <SelfCheckinPageSubtitle {...subtitle} />}
            {children}
          </div>
        )}
      </main>
      {footer && <Footer {...footer} />}
    </div>
  )
}

const rootStyle = css({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  color: '#272727',
  '*': { fontFamily: 'Noto Sans JP' },
})

const mainStyle = css({
  boxSizing: 'border-box',
  width: '100%',
  flex: 1,
})

const standardMainStyle = css(
  {
    backgroundColor: '#F2F2F2',
    paddingTop: FrontTabletResponsiveToken.headerHeight.landscape11,
  },
  createSelfCheckinResponsiveStyle({
    portrait: { paddingTop: FrontTabletResponsiveToken.headerHeight.portrait },
    landscape13: { paddingTop: FrontTabletResponsiveToken.headerHeight.landscape13 },
  }),
)

const fullscreenMainStyle = css({
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
})

const mainWithFooterStyle = css(
  { paddingBottom: 136 },
  createSelfCheckinResponsiveStyle({
    landscape13: { paddingBottom: 142 },
  }),
)

const contentStyle = css(
  {
    width: '90%',
    maxWidth: 1062,
    marginInline: 'auto',
    paddingTop: 32,
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },
  createSelfCheckinResponsiveStyle({
    portrait: {
      width: '100%',
      maxWidth: 820,
    },
    landscape13: {
      width: '90%',
      maxWidth: 1230,
    },
  }),
)
